import { Button, Modal, Radio, Textarea } from '@any-ui-react/core';
import { useEffect, useMemo, useState } from 'react';
import { AlertBanner } from '@/common/components';
import { ModalFooter } from '@/common/components/shared/modal-footer';
import { OrdinalNumber } from '@/common/components/shared/ordinal-number';
import { toast } from '@/common/components/shared/toaster';
import {
  ProbationReviewOutcome,
  ProbationReviewReviewStatus,
  useRecordProbationReviewOutcomeMutation,
} from '@/common/graphql';
import {
  ProbationReviewOutcomeLabels,
  ProbationReviewRecordOutcomeFormData,
  type ProbationReviewType,
} from '@/common/types';
import { ApiUtils, DATETIME_FORMATS, DateUtils, evictProbationReviewAuditLogs } from '@/common/utils';

interface Props {
  opened: boolean;
  /** The employee the outcome is recorded for. */
  target: ProbationReviewType | null;
  onClose: () => void;
  /** Called once the outcome is saved, so the list can reload. */
  onCompleted: () => void;
}

const OUTCOMES = [ProbationReviewOutcome.Passed, ProbationReviewOutcome.Failed] as const;

export const RecordOutcomeDialog = ({ opened, target, onClose, onCompleted }: Props) => {
  const [outcome, setOutcome] = useState<ProbationReviewOutcome | null>(null);
  const [comment, setComment] = useState('');
  const [error, setError] = useState<string | null>(null);

  const [recordOutcome, { loading }] = useRecordProbationReviewOutcomeMutation({
    onError(apiError) {
      ApiUtils.handleApiError(apiError);
    },
  });

  useEffect(() => {
    if (opened) {
      setOutcome(null);
      setComment('');
      setError(null);
    }
  }, [opened]);

  // Saving the outcome completes the probation, so the reviews that were never submitted are closed by it.
  const reviewsToClose = useMemo(
    () =>
      (target?.reviews ?? [])
        .map((review, index) => ({ ...review, number: index + 1 }))
        .filter(review => review.reviewStatus === ProbationReviewReviewStatus.Draft),
    [target],
  );

  const handleSubmit = async () => {
    const parsed = ProbationReviewRecordOutcomeFormData.safeParse({ outcome, comment });
    if (!parsed.success || !parsed.data.outcome || !target) {
      setError(parsed.success ? null : (parsed.error.issues[0]?.message ?? null));
      return;
    }
    setError(null);

    const { data } = await recordOutcome({
      variables: {
        input: {
          employeeId: target.employeeId,
          outcome: parsed.data.outcome,
          comment: parsed.data.comment.trim() || null,
        },
      },
      update: cache => evictProbationReviewAuditLogs(cache, [target.employeeId]),
    });
    if (!data?.recordProbationReviewOutcome.id) {
      return;
    }

    // The outcome is recorded either way; only the notification may have failed.
    if (data.recordProbationReviewOutcome.notificationFailed) {
      toast.warning('Outcome recorded, but the notification email could not be sent. Please send it by hand.');
    } else {
      toast.success('Outcome recorded');
    }
    onCompleted();
    onClose();
  };

  return (
    <Modal
      opened={opened}
      title={`Record Outcome${target ? `: ${target.employeeName}` : ''}`}
      size="lg"
      onClose={onClose}
    >
      <div className="flex flex-col gap-4">
        <Radio.Group
          error={error}
          label="Outcome"
          value={outcome}
          withAsterisk={true}
          onChange={value => {
            setOutcome(value as ProbationReviewOutcome);
            setError(null);
          }}
        >
          <div className="flex flex-col gap-2 pt-2">
            {OUTCOMES.map(value => (
              <Radio key={value} label={ProbationReviewOutcomeLabels[value]} value={value} />
            ))}
          </div>
        </Radio.Group>

        <Textarea
          autosize={true}
          label="Comment"
          minRows={3}
          placeholder="Optional. Shown in the export and the audit log."
          value={comment}
          onChange={event => setComment(event.currentTarget.value)}
        />

        <div className="flex flex-col gap-2 text-sm">
          {reviewsToClose.length > 0 ? (
            <>
              <p>{`${reviewsToClose.length} unsubmitted ${reviewsToClose.length === 1 ? 'review' : 'reviews'} will be closed:`}</p>
              <div className="grid grid-cols-[auto_1fr]">
                <ul>
                  {reviewsToClose.map(review => (
                    <li key={review.number} className="grid grid-cols-[auto_auto] gap-4">
                      <div className="text-gray-7">
                        <OrdinalNumber value={review.number} />
                        &nbsp;review
                      </div>
                      <div className="tabular-nums text-right">
                        {DateUtils.formatDate(review.deadline, DATETIME_FORMATS.MMMddyyyy)}
                      </div>
                    </li>
                  ))}
                </ul>
                <div />
              </div>
            </>
          ) : (
            <p>There are no unsubmitted reviews to close.</p>
          )}
          <AlertBanner type="warning" className="shadow-none">
            Recording the outcome completes the probation and cannot be undone.
          </AlertBanner>
        </div>

        <ModalFooter spacerSize={-1.5}>
          <Button variant="default" onClick={onClose}>
            Cancel
          </Button>
          <Button loading={loading} onClick={handleSubmit}>
            Record Outcome
          </Button>
        </ModalFooter>
      </div>
    </Modal>
  );
};
